import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Tag, Plus, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CardHistoryItem {
  id: string;
  card_title: string;
  card_description: string;
  board_name: string;
  list_name: string;
  assignee_name?: string;
  due_date?: string;
  priority?: 'low' | 'medium' | 'high';
  labels: string[];
  created_at: string;
}

export default function TaskHistory() {
  const [cards, setCards] = useState<CardHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    loadCardHistory();
  }, [currentPage]);

  const loadCardHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        setError('Please sign in to view your card history');
        return;
      }

      // Get total count for pagination
      const { count } = await supabase
        .from('card_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setTotalCount(count || 0);

      if (count === 0) {
        setCards([]);
        return;
      }

      // Get paginated results
      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data, error } = await supabase
        .from('card_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        throw error;
      }

      setCards(data || []);
    } catch (err) {
      console.error('Failed to load card history:', err);
      setError('Failed to load card history');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
          <span className="ml-2">Loading card history...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-12">
          <div className="text-red-600 text-lg mb-4">{error}</div>
          <button
            onClick={loadCardHistory}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Recent Trello Cards</h1>
          <p className="text-gray-600">
            {totalCount > 0 
              ? `${totalCount} card${totalCount === 1 ? '' : 's'} created from your meeting notes`
              : 'View your Trello card creation history'
            }
          </p>
        </div>
        <Link
          to="/dashboard"
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create New Cards</span>
        </Link>
      </div>

      {cards.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Trello cards created yet</h3>
          <p className="text-gray-600 mb-6">
            Start by uploading meeting notes to create your first Trello cards.
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700"
          >
            <Plus className="w-4 h-4" />
            <span>Upload meeting notes</span>
          </Link>
        </div>
      ) : (
        <>
          {/* Card List */}
          <div className="space-y-4">
            {cards.map((card) => (
              <div key={card.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">{card.card_title}</h3>
                      {card.priority && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(card.priority)}`}>
                          {card.priority}
                        </span>
                      )}
                    </div>
                    
                    {card.card_description && (
                      <p className="text-gray-600 mb-4">{card.card_description}</p>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                      {card.assignee_name && (
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">{card.assignee_name}</span>
                        </div>
                      )}
                      
                      {card.due_date && (
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Due {new Date(card.due_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Created {formatDate(card.created_at)}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600">{card.board_name} → {card.list_name}</span>
                      </div>
                    </div>
                    
                    {card.labels.length > 0 && (
                      <div className="flex items-center space-x-2 mt-3">
                        <Tag className="w-4 h-4 text-gray-400" />
                        <div className="flex flex-wrap gap-1">
                          {card.labels.map((label, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} cards
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm border rounded-md ${
                        page === currentPage
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Summary Stats */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
              <div className="text-sm text-gray-600">Total Cards Created</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {new Set(cards.map(c => c.board_name)).size}
              </div>
              <div className="text-sm text-gray-600">Boards Used</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {cards.filter(c => c.assignee_name).length}
              </div>
              <div className="text-sm text-gray-600">Cards with Assignees</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
} 